import { useEffect, useState } from "react";
import { Mail, MailCheck, MailX } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { publicShare, digest as digestApi } from "../lib/api";
import { toast } from "sonner";

const EMAIL_KEY = "verihandi_digest_email";

export default function DigestSubscribeCard({ token }) {
  const [configured, setConfigured] = useState(null); // null=loading, true/false
  const [email, setEmail] = useState(() => localStorage.getItem(EMAIL_KEY) || "");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    digestApi.status().then((s) => setConfigured(!!s.configured)).catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    if (!configured || !email) return;
    publicShare.digestSubscription(token, email).then((r) => setSubscribed(!!r.subscribed)).catch(() => {});
  }, [configured, email, token]);

  if (configured === null) return null;
  if (configured === false) return null; // silently hide if Resend isn't configured

  async function subscribe() {
    const e = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Please enter a valid email"); return; }
    setBusy(true);
    try {
      await publicShare.digestSubscribe(token, e, localStorage.getItem("handy_caregiver_name") || undefined);
      localStorage.setItem(EMAIL_KEY, e);
      setSubscribed(true);
      toast.success("You're subscribed. A welcome email is on the way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  }

  async function unsubscribe() {
    setBusy(true);
    try {
      await publicShare.digestUnsubscribe(token, email);
      setSubscribed(false);
      toast.success("Unsubscribed");
    } catch {
      toast.error("Could not unsubscribe");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card-tactile rounded-2xl p-5" data-testid="digest-subscribe-section">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
          {subscribed ? <MailCheck className="h-5 w-5 text-[color:var(--primary-ink)]" /> : <Mail className="h-5 w-5 text-[color:var(--text-muted)]" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-lg tracking-tight">Weekly email digest</h3>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">
            {subscribed
              ? <>A short adherence summary will arrive every Monday at <b>{email}</b>. No push permission needed — works on any browser or Mail app.</>
              : <>Get a short weekly adherence summary by email — no push permission required. Every Monday morning.</>}
          </p>
          {!subscribed ? (
            <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
              <Input
                type="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="digest-email-input"
                className="sm:max-w-xs"
                autoComplete="email"
              />
              <Button
                onClick={subscribe}
                disabled={busy || !email}
                data-testid="digest-subscribe-button"
                className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
              >
                {busy ? "Subscribing…" : "Subscribe"}
              </Button>
            </div>
          ) : (
            <div className="mt-3">
              <Button
                onClick={unsubscribe}
                disabled={busy}
                variant="outline"
                className="border-[color:var(--border-subtle)]"
                data-testid="digest-unsubscribe-button"
              >
                <MailX className="h-4 w-4 mr-2" /> {busy ? "Unsubscribing…" : "Unsubscribe"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
